/** t3code's numbers, so the two products move at the same speed. */
export const RESTING_TRANSITION_DURATION_MS = 280;
const RESTING_TRANSITION_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";
const RESTING_CONTROLS_ARRIVAL_DRIFT_PX = 4;
/**
 * A suspended document timeline can leave `finished` pending forever while the
 * pinned height is still in place. Wall-clock cleanup makes natural layout the
 * eventual source of truth in that case.
 */
export const RESTING_TRANSITION_CLEANUP_BUFFER_MS = 50;

/** Smallest height change worth a tween. Below it the move is not visible. */
const MEANINGFUL_HEIGHT_CHANGE_PX = 0.5;

export function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/**
 * jsdom and older engines have no Web Animations API. Every call site treats a
 * missing `animate` the same way it treats the reduced-motion preference: the
 * layout still changes, it just changes at once.
 */
export function canAnimate(element: HTMLElement | null): element is HTMLElement {
  return element !== null && typeof element.animate === "function";
}

export function isHeightChangeWorthAnimating(
  previousHeight: number | null,
  nextHeight: number,
): previousHeight is number {
  return (
    previousHeight !== null && Math.abs(previousHeight - nextHeight) >= MEANINGFUL_HEIGHT_CHANGE_PX
  );
}

/** The outer height tween, from the height the Composer was actually showing. */
function animateComposerHeight(
  form: HTMLElement,
  previousHeight: number,
  nextHeight: number,
): Animation {
  return form.animate(
    [{ height: `${String(previousHeight)}px` }, { height: `${String(nextHeight)}px` }],
    { duration: RESTING_TRANSITION_DURATION_MS, easing: RESTING_TRANSITION_EASING },
  );
}

/** The footer cluster the relocated controls return to when the Composer expands. */
const FOOTER_CONTROL_DOCK_SELECTOR = '[data-chat-composer-icon-dock="true"]';

/**
 * The arriving controls, fading in along their direction of travel.
 *
 * They teleport between the footer and the strip in one commit, so without
 * this the move reads as a pop. Controls landing in the strip can appear at
 * once because the space below the Composer is already empty, and they take
 * the whole tween. Controls returning to the footer wait out its first half,
 * because until then the row they are returning to is still occupied by the
 * prompt, so they fade in over the second half.
 */
function animateRestingControlsArrival(controls: HTMLElement, isResting: boolean): Animation {
  const drift = isResting ? -RESTING_CONTROLS_ARRIVAL_DRIFT_PX : RESTING_CONTROLS_ARRIVAL_DRIFT_PX;
  return controls.animate(
    [
      { opacity: 0, transform: `translateY(${String(drift)}px)` },
      { opacity: 1, transform: "none" },
    ],
    {
      duration: isResting ? RESTING_TRANSITION_DURATION_MS : RESTING_TRANSITION_DURATION_MS / 2,
      delay: isResting ? 0 : RESTING_TRANSITION_DURATION_MS / 2,
      fill: "backwards",
      easing: RESTING_TRANSITION_EASING,
    },
  );
}

export interface RestingComposerTransitionAnimations {
  readonly height: Animation;
  readonly arrival: Animation | null;
  readonly content: readonly Animation[];
}

/**
 * Start the whole transition: the outer height tween, plus the arriving
 * controls when the layout state is what changed.
 *
 * Together rather than at the call site so the hook holds the lifecycle, which
 * is the part with the interruption and cleanup rules, and this holds what
 * moves.
 */
export function startRestingComposerTransition(input: {
  readonly form: HTMLElement;
  readonly controls: HTMLElement | null;
  readonly previousHeight: number;
  readonly nextHeight: number;
  readonly isResting: boolean;
  readonly stateChanged: boolean;
  readonly previousGeometry?: ComposerContentGeometry | null;
}): RestingComposerTransitionAnimations {
  // Expanding returns the controls to the footer's dock, so that is what
  // fades in. Fading the form instead would take the prompt, the send cluster
  // and the whole box to zero opacity for the first half of every expand.
  const arriving = input.isResting
    ? input.controls
    : input.form.querySelector<HTMLElement>(FOOTER_CONTROL_DOCK_SELECTOR);
  const animateArrival = input.stateChanged && canAnimate(arriving);
  const height = animateComposerHeight(input.form, input.previousHeight, input.nextHeight);
  return {
    height,
    content: animateComposerContent(input.form, input.previousGeometry ?? null, input.isResting),
    arrival: animateArrival ? animateRestingControlsArrival(arriving, input.isResting) : null,
  };
}

export interface ComposerContentGeometry {
  readonly promptFromTop: number | null;
  readonly promptHeight: number | null;
  readonly actionFromBottom: number | null;
}

export function readComposerContentGeometry(surface: HTMLElement): ComposerContentGeometry {
  const rect = surface.getBoundingClientRect();
  const prompt = surface
    .querySelector<HTMLElement>('[data-testid="composer-editor"]')
    ?.getBoundingClientRect();
  const action = surface
    .querySelector<HTMLElement>('[data-chat-composer-actions="right"]')
    ?.getBoundingClientRect();
  return {
    promptFromTop: prompt ? prompt.top - rect.top : null,
    promptHeight: prompt?.height ?? null,
    actionFromBottom: action ? rect.bottom - action.top : null,
  };
}

function animateComposerContent(
  surface: HTMLElement,
  previous: ComposerContentGeometry | null,
  resting: boolean,
): Animation[] {
  if (!previous) return [];
  const rect = surface.getBoundingClientRect();
  const prompt = surface.querySelector<HTMLElement>('[data-testid="composer-editor"]');
  const action = surface.querySelector<HTMLElement>('[data-chat-composer-actions="right"]');
  const animations: Animation[] = [];
  const move = (element: HTMLElement | null, top: number | null) => {
    if (!element || top === null) return;
    const offset = top - element.getBoundingClientRect().top;
    if (Math.abs(offset) < 0.5) return;
    animations.push(
      element.animate([{ transform: `translateY(${offset}px)` }, { transform: "none" }], {
        duration: RESTING_TRANSITION_DURATION_MS,
        easing: RESTING_TRANSITION_EASING,
      }),
    );
  };
  move(prompt, previous.promptFromTop === null ? null : rect.top + previous.promptFromTop);
  move(action, previous.actionFromBottom === null ? null : rect.bottom - previous.actionFromBottom);
  if (!resting && prompt && previous.promptHeight !== null) {
    const hidden = prompt.getBoundingClientRect().height - previous.promptHeight;
    if (hidden > 0.5)
      animations.push(
        prompt.animate([{ clipPath: `inset(0 0 ${hidden}px 0)` }, { clipPath: "inset(0)" }], {
          duration: RESTING_TRANSITION_DURATION_MS,
          easing: RESTING_TRANSITION_EASING,
        }),
      );
  }
  animations.push(...animateImageArrival(surface, resting));
  return animations;
}

function animateImageArrival(surface: HTMLElement, resting: boolean): Animation[] {
  const animations: Animation[] = [];
  for (const image of surface.querySelectorAll<HTMLElement>(
    '[data-chat-composer-images="true"] > *',
  )) {
    animations.push(
      image.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: resting ? RESTING_TRANSITION_DURATION_MS : RESTING_TRANSITION_DURATION_MS / 2,
        delay: resting ? 0 : RESTING_TRANSITION_DURATION_MS / 2,
        fill: "backwards",
        easing: RESTING_TRANSITION_EASING,
      }),
    );
  }
  return animations;
}
